import type { GridPagination } from "@repo/ui/grid/types";
import type { SetStateAction } from "react";

import { noValue } from "@repo/helpers";
import { useCallback, useEffect, useRef, useState } from "react";

import { useCloudSettingsQuery } from "../api/settingsApi";

const PAGING_KEYS = new Set(["currentPage", "itemsPerPage"]);
/** Set on the applied filters by a column-header click; the draft never has them. */
const SORT_KEYS = ["sortBy", "sortBy.name", "sortBy.order"];
const TYPED_INPUT_TYPES = new Set(["text", "search", "email", "tel", "url", "number"]);

function sameFilterValue(a: unknown, b: unknown) {
	if (noValue(a) && noValue(b)) return true;
	return JSON.stringify(a) === JSON.stringify(b);
}

export function hasFilterChanges<T extends GridPagination>(draft: T, applied: T) {
	return Object.keys(draft).some((key) => !PAGING_KEYS.has(key) && !sameFilterValue(draft[key], applied[key]));
}

function gridSort(applied: object) {
	return Object.fromEntries(SORT_KEYS.filter((key) => key in applied).map((key) => [key, applied[key]]));
}

/** True for a text box the user types a filter into. React-select's search input is a combobox and doesn't count. */
export function isTypingIn(element: Element | null) {
	if (element instanceof HTMLTextAreaElement) return !element.readOnly;
	if (!(element instanceof HTMLInputElement) || element.readOnly) return false;
	return element.getAttribute("role") !== "combobox" && TYPED_INPUT_TYPES.has(element.type);
}

/**
 * Draft filters the user is editing, and the applied filters the grid queries and exports.
 * Spread `filtersProps` into FiltersV2. With the Grids.ApplyFiltersOnBlur setting on, typed filters apply when
 * focus leaves them and every other filter (dropdowns, dates, toggles) applies as soon as it changes.
 * With it off, drafts apply only on Find.
 */
export function useGridFilters<T extends GridPagination>(initialFilters: T) {
	const { settings } = useCloudSettingsQuery();
	const applyOnBlur = settings?.applyGridFiltersOnBlur ?? true;
	const [filters, setDraftFilters] = useState<T>(initialFilters);
	const [appliedFilters, setAppliedFilters] = useState<T>(initialFilters);

	// Refs, not state, because a date picker commits typed text in the same blur event that applies the filters.
	const latestFilters = useRef(initialFilters);
	const latestApplyOnBlur = useRef(applyOnBlur);
	useEffect(() => {
		latestApplyOnBlur.current = applyOnBlur;
	}, [applyOnBlur]);

	const applyFilters = useCallback(() => {
		const draft = latestFilters.current;
		setAppliedFilters((current) =>
			hasFilterChanges(draft, current)
				? { ...gridSort(current), ...draft, currentPage: 1, itemsPerPage: current.itemsPerPage }
				: current,
		);
	}, []);

	const setFilters = useCallback(
		(update: SetStateAction<T>) => {
			latestFilters.current = typeof update === "function" ? update(latestFilters.current) : update;
			setDraftFilters(latestFilters.current);
			if (latestApplyOnBlur.current && !isTypingIn(document.activeElement)) applyFilters();
		},
		[applyFilters],
	);

	/** Updates the draft and applies it right away, even when drafts otherwise wait for Find (e.g. a date quick range). */
	const applyNow = useCallback(
		(update: SetStateAction<T>) => {
			latestFilters.current = typeof update === "function" ? update(latestFilters.current) : update;
			setDraftFilters(latestFilters.current);
			applyFilters();
		},
		[applyFilters],
	);

	return {
		filters,
		setFilters,
		applyNow,
		appliedFilters,
		setAppliedFilters,
		filtersProps: { onApply: applyFilters, applyOnBlur },
	};
}
